import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, CalendarClock, CalendarPlus, CalendarRange, Check, ChevronRight, Flag, Flame, Pencil, Target, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { BotonEnlace, Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, Dato, EstadoVacio, Pastilla, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { DateField, NumberField, SelectField, TextField } from '@/components/Field';
import { Fila, Filas } from '@/components/Fila';
import { leerSettings } from '@/db/schema';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import { leerMetasDelDia } from '@/db/metas';
import {
  agregarCiclos,
  borrarPlan,
  cambiarFinDeEtapa,
  cambiarMetas,
  crearPlan,
  leerPlanes,
  mensajeTraslape,
  planActivo,
  revisarPlanNuevo,
  sePuedeBorrar,
  terminarPlan,
} from '@/db/planes';
import type { EtapaPlantilla, Plan, Stage } from '@/db/types';
import { diferenciaDias, esFechaLocal, formatearCortoConAnio, maxima, sumarDias, type FechaLocal } from '@/domain/dates';
import {
  NOMBRE_ETAPA,
  PLANTILLAS,
  PLAN_DE_JANETH,
  avisoFinDeProgramacion,
  esPerfilDeJaneth,
  etapaDe,
  etapasConFinCambiable,
  finProgramado,
  plantillaPorId,
  posicionEnPlan,
  primerTraslape,
  programar,
  recorrerAlCambiarFin,
  textoDelPlanDeJaneth,
  textoMetaKcal,
  textoPosicion,
  type CambioDeFin,
  type EtapaProgramada,
  type MetasDelDia,
} from '@/domain/etapas';
import { formatearGramos, formatearKcal } from '@/domain/nutricion';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { CamposMetas, estadoDeMetas, metasDeEstado, type EstadoMetas } from './CamposMetas';
import { Opciones } from '@/features/food/comunes';

const rango = (inicio: FechaLocal, fin: FechaLocal | null) =>
  `${formatearCortoConAnio(inicio)} – ${fin === null ? 'sin fin' : formatearCortoConAnio(fin)}`;

function textoMacros(m: Pick<EtapaPlantilla, 'proteinaG' | 'carbohidratosG' | 'grasaG' | 'ayunoHoras'>): string {
  const partes = [
    m.proteinaG === null ? null : `P ${formatearGramos(m.proteinaG)}`,
    m.carbohidratosG === null ? null : `C ${formatearGramos(m.carbohidratosG)}`,
    m.grasaG === null ? null : `G ${formatearGramos(m.grasaG)}`,
    m.ayunoHoras === null ? null : `ayuno ${m.ayunoHoras} h`,
  ].filter(Boolean);
  return partes.length === 0 ? 'Sin metas de macros ni de ayuno' : partes.join(' · ');
}

/** Las metas de hoy, en renglones (Req. 21.6, 21.7). */
export function MetasDeHoy({ metas }: { metas: MetasDelDia }) {
  const gramos = (g: number | null) => (g === null ? 'Sin meta' : formatearGramos(g));
  return (
    <dl className="divide-y divide-borde">
      <Dato
        etiqueta="Calorías"
        valor={
          metas.kcal !== null ? (
            formatearKcal(metas.kcal)
          ) : metas.faltaMantenimiento ? (
            <Link to="/mas/mantenimiento" className="text-marca">
              Falta el mantenimiento
            </Link>
          ) : (
            'Sin meta'
          )
        }
        {...(metas.mantenimientoKcal !== null
          ? { detalle: `Con tu mantenimiento de ${formatearKcal(metas.mantenimientoKcal)}` }
          : {})}
      />
      <Dato etiqueta="Proteína" valor={gramos(metas.proteinaG)} />
      <Dato etiqueta="Carbohidratos" valor={gramos(metas.carbohidratosG)} />
      <Dato etiqueta="Grasa" valor={gramos(metas.grasaG)} />
      <Dato etiqueta="Ayuno" valor={metas.ayunoHoras === null ? 'Sin meta' : `${metas.ayunoHoras} h`} />
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Mi plan
// ---------------------------------------------------------------------------

export function PlanPage() {
  const hoy = useFechaHoy();
  const datos = useLiveQuery(() => leerPlanes(), []);
  const deHoy = useLiveQuery(() => leerMetasDelDia(hoy), [hoy]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  if (datos === undefined || deHoy === undefined || ajustes === undefined) return null;

  const activo = planActivo(datos.planes);
  const pasados = datos.planes.filter((p) => p.estado === 'terminado');

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Tu plan."
        descripcion="La etapa de cada día y sus metas. Lo que ya pasó conserva las suyas."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      {activo === null && datos.planes.length === 0 && esPerfilDeJaneth(ajustes.nombre) && <PlanAcordado />}

      {activo === null ? (
        <SinPlan metas={deHoy.metas} />
      ) : (
        <PlanActivo plan={activo} etapas={datos.etapas.filter((e) => e.planId === activo.id)} metas={deHoy.metas} hoy={hoy} />
      )}

      {pasados.length > 0 && (
        <Seccion titulo="Planes anteriores">
          <ul className="divide-y divide-borde">
            {pasados.map((p) => (
              <li key={p.id} className="py-3">
                <p className="text-[14px] font-medium text-texto">{p.nombre}</p>
                <p className="text-[12px] text-texto-suave">{rango(p.fechaInicio, p.fechaFin)}</p>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      <Filas className="border-t border-borde">
        <Fila icono={Flame} titulo="Mantenimiento" detalle="Tu mantenimiento capturado a mano" a="/mas/mantenimiento" />
        <Fila icono={Target} titulo="Metas sin plan" detalle="Para los días que no cubre un plan" a="/mas/metas" />
      </Filas>
    </div>
  );
}

/**
 * En el teléfono de Janeth sin ningún plan (0.3.8): su plan acordado, con los
 * valores y las fechas confirmados, listo para revisarlo y crearlo. No se
 * crea solo; las metas del mantenimiento siguen pendientes.
 */
export function PlanAcordado() {
  return (
    <Card>
      <span className="antetitulo">Tu plan acordado</span>
      <h2 className="mt-2 text-[17px] leading-snug tracking-[-0.25px] text-texto">Listo para crearlo</h2>
      <p className="mt-1.5 text-[13px] leading-relaxed text-texto-suave">{textoDelPlanDeJaneth()}</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-texto-suave">
        No se crea solo. Al crearlo con su fecha de inicio, tus días desde entonces quedan en su etapa.
      </p>
      <div className="mt-4">
        <BotonEnlace to={`/mas/plan/nuevo?plantilla=${PLAN_DE_JANETH.plantilla}`} icono={ArrowRight}>
          Revisar y crear mi plan
        </BotonEnlace>
      </div>
    </Card>
  );
}

function SinPlan({ metas }: { metas: MetasDelDia }) {
  return (
    <>
      <EstadoVacio
        icono={CalendarRange}
        titulo="Sin plan activo."
        descripcion="Mientras no haya plan, la app compara cada día con tus metas sin plan. Ninguna plantilla se activa sola."
      />
      <Seccion titulo="Hoy se usan tus metas sin plan">
        <MetasDeHoy metas={metas} />
      </Seccion>
      <Seccion titulo="Plantillas" descripcion="Crear un plan copia la plantilla: editar el plan no la cambia.">
        <ul className="divide-y divide-borde">
          {PLANTILLAS.map((p) => (
            <li key={p.id}>
              <Link to={`/mas/plan/nuevo?plantilla=${p.id}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="text-[14px] font-medium text-texto">{p.nombre}</span>
                  <span className="text-[12px] text-texto-suave">{p.descripcion}</span>
                </span>
                <ChevronRight className="size-4 text-texto-suave" />
              </Link>
            </li>
          ))}
        </ul>
      </Seccion>
    </>
  );
}

function PlanActivo({ plan, etapas, metas, hoy }: { plan: Plan; etapas: Stage[]; metas: MetasDelDia; hoy: FechaLocal }) {
  const navegar = useNavigate();
  const [verTodo, setVerTodo] = useState(false);
  const [terminando, setTerminando] = useState(false);
  const [ultimoDia, setUltimoDia] = useState(hoy);
  const [borrando, setBorrando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const posicion = posicionEnPlan(etapas, hoy);
  const fin = finProgramado(etapas);
  const avisoFin = avisoFinDeProgramacion(plan, etapas, hoy);
  const ciclos = [...new Set(etapas.map((e) => e.ciclo))].sort((a, b) => a - b);
  const cicloActual = etapaDe(etapas, hoy)?.ciclo ?? ciclos[0] ?? 1;
  const visibles = verTodo ? ciclos : ciclos.filter((c) => c >= cicloActual).slice(0, 3);

  async function hacer(accion: () => Promise<unknown>, despues?: () => void) {
    setOcupado(true);
    setError(null);
    try {
      await accion();
      despues?.();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Card>
        <div className="flex items-center justify-between gap-2">
          <span className="antetitulo">Plan activo</span>
          <Pastilla>Desde {formatearCortoConAnio(plan.fechaInicio)}</Pastilla>
        </div>
        <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">{plan.nombre}</h2>
        <p className="text-[13px] leading-relaxed text-texto">
          {posicion === null ? 'Hoy no hay una etapa programada.' : textoPosicion(posicion)}
        </p>
        {fin !== null && (
          <p className="mt-1 text-[12px] text-texto-suave">Programado hasta el {formatearCortoConAnio(fin)}.</p>
        )}
      </Card>

      {avisoFin !== null && (
        <Banner
          tono="aviso"
          titulo={`Lo programado termina el ${formatearCortoConAnio(avisoFin.fin)}`}
          accion={
            <Button
              variante="secundario"
              icono={CalendarPlus}
              disabled={ocupado}
              onClick={() =>
                void hacer(async () => {
                  const n = await agregarCiclos(plan.id);
                  setAviso(`Se agregaron ${n} ciclos.`);
                })
              }
            >
              Agregar otro año de ciclos
            </Button>
          }
        >
          {avisoFin.diasQueFaltan === 1 ? 'Falta 1 día.' : `Faltan ${avisoFin.diasQueFaltan} días.`}
        </Banner>
      )}
      {aviso !== null && <Banner tono="exito" titulo={aviso} />}

      <Seccion titulo="Metas de hoy">
        <MetasDeHoy metas={metas} />
      </Seccion>

      <Seccion titulo="Metas por etapa" descripcion="Al editarlas aplican desde hoy; los días anteriores conservan las suyas.">
        <ul className="divide-y divide-borde">
          {plan.plantilla.map((p, indice) => (
            <li key={indice} className="flex items-center gap-3 py-3">
              <span className="grid min-w-0 flex-1 gap-1">
                <span className="text-[14px] font-medium text-texto">
                  {NOMBRE_ETAPA[p.tipo]}
                  {p.semanas === null ? '' : ` · ${p.semanas} ${p.semanas === 1 ? 'semana' : 'semanas'}`}
                </span>
                <span className="text-[12px] text-texto-suave">{textoMetaKcal(p.metaKcal)}</span>
                <span className="text-[12px] text-texto-suave">{textoMacros(p)}</span>
              </span>
              <Link
                to={`/mas/plan/metas?indice=${indice}`}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-marca-suave px-3 text-[13px] font-medium text-texto"
              >
                <Pencil className="size-4" />
                Editar{' '}
                <span className="sr-only">las metas de {NOMBRE_ETAPA[p.tipo].toLowerCase()}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Seccion>

      <Seccion titulo="Calendario" extra={`${ciclos.length} ${ciclos.length === 1 ? 'ciclo' : 'ciclos'}`}>
        <ol className="divide-y divide-borde">
          {visibles.map((ciclo) => (
            <li key={ciclo} className="py-3">
              <p className="flex items-center gap-2 text-[14px] font-medium text-texto">
                Ciclo {ciclo}
                {ciclo === cicloActual && posicion !== null && <Pastilla>Ahora</Pastilla>}
              </p>
              <ul className="mt-1 space-y-0.5">
                {etapas
                  .filter((e) => e.ciclo === ciclo)
                  .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio))
                  .map((e) => (
                    <li key={e.id} className="text-[12px] text-texto-suave">
                      {NOMBRE_ETAPA[e.tipo]}: {rango(e.fechaInicio, e.fechaFin)}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ol>
        {ciclos.length > visibles.length || verTodo ? (
          <button
            type="button"
            aria-expanded={verTodo}
            onClick={() => setVerTodo((v) => !v)}
            className="inline-flex min-h-11 items-center text-[13px] font-medium text-marca"
          >
            {verTodo ? 'Ver menos' : `Ver los ${ciclos.length} ciclos`}
          </button>
        ) : null}
        {etapasConFinCambiable(etapas, hoy).length > 0 && (
          <BotonEnlace to="/mas/plan/fin" variante="secundario" icono={CalendarClock}>
            Cambiar el fin de una etapa
          </BotonEnlace>
        )}
      </Seccion>

      <Seccion titulo="Cambiar de plan">
        {terminando ? (
          <div className="space-y-3 rounded-panel bg-superficie p-5">
            {/* Solo hoy o ayer: una fecha anterior borraría metas de días ya vividos */}
            <Opciones
              etiqueta="Último día del plan"
              valor={ultimoDia === hoy ? 'hoy' : 'ayer'}
              onCambio={(v) => setUltimoDia(v === 'hoy' ? hoy : sumarDias(hoy, -1))}
              columnas={2}
              opciones={[
                { valor: 'hoy', etiqueta: 'Hoy' },
                { valor: 'ayer', etiqueta: 'Ayer' },
              ]}
            />
            <p className="text-[12px] leading-relaxed text-texto-suave">
              Lo que ya pasó se conserva con sus metas. Las etapas que todavía no empiezan se quitan.
              Tus comidas no cambian.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variante="peligro"
                disabled={ocupado}
                onClick={() => void hacer(() => terminarPlan(plan.id, ultimoDia, hoy), () => setTerminando(false))}
              >
                Terminar
              </Button>
              <Button variante="secundario" onClick={() => setTerminando(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variante="secundario" icono={Flag} onClick={() => setTerminando(true)}>
            Terminar este plan
          </Button>
        )}

        {sePuedeBorrar(plan, hoy) &&
          (borrando ? (
            <div className="space-y-3 rounded-panel bg-alerta-suave p-4">
              <p className="text-[13px] text-texto">
                ¿Borrar el plan? Úsalo si lo creaste por error: se quitan sus etapas. Tus comidas no cambian.
                {plan.reemplazo !== null && plan.reemplazo !== undefined
                  ? ' El plan que terminó al crearlo vuelve tal como estaba.'
                  : ''}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variante="peligro"
                  disabled={ocupado}
                  onClick={() => void hacer(() => borrarPlan(plan.id, hoy), () => void navegar('/mas/plan', { replace: true }))}
                >
                  Sí, borrar
                </Button>
                <Button variante="secundario" onClick={() => setBorrando(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variante="fantasma" icono={Trash2} onClick={() => setBorrando(true)}>
              Borrar el plan (creado hoy)
            </Button>
          ))}

        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        )}
      </Seccion>
    </>
  );
}

// ---------------------------------------------------------------------------
// Crear un plan desde una plantilla (Req. 21.1 a 21.5)
// ---------------------------------------------------------------------------

export function NuevoPlanPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const datos = useLiveQuery(() => leerPlanes(), []);
  const [parametros] = useSearchParams();
  const plantilla = plantillaPorId(parametros.get('plantilla') ?? '') ?? PLANTILLAS[0];
  if (ajustes === undefined || datos === undefined || plantilla === undefined) return null;
  const prellenar = plantilla.id === PLAN_DE_JANETH.plantilla && esPerfilDeJaneth(ajustes.nombre);
  return <FormularioPlan key={plantilla.id} plantillaId={plantilla.id} prellenar={prellenar} activo={planActivo(datos.planes)} />;
}

function FormularioPlan({ plantillaId, prellenar, activo }: { plantillaId: string; prellenar: boolean; activo: Plan | null }) {
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  const plantilla = plantillaPorId(plantillaId);
  const [id] = useState(() => nuevoId('plan'));
  const [nombre, setNombre] = useState(plantilla?.nombre ?? '');
  // Con un plan activo, el nuevo empieza hoy o después (planes.ts, revisar)
  const [fechaInicio, setFechaInicio] = useState<string>(
    prellenar && activo === null ? PLAN_DE_JANETH.fechaInicio : hoy,
  );
  const [repetir, setRepetir] = useState(plantilla?.repetir ?? false);
  const [semanas, setSemanas] = useState<string[]>(plantilla?.etapas.map((e) => (e.semanas === null ? '' : String(e.semanas))) ?? []);
  const [metas, setMetas] = useState<EstadoMetas[]>(
    plantilla?.etapas.map((e, i) => estadoDeMetas(prellenar ? { ...e, ...PLAN_DE_JANETH.metas[i] } : e)) ?? [],
  );
  const [confirmarTermino, setConfirmarTermino] = useState(false);
  const [pideConfirmar, setPideConfirmar] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (plantilla === undefined) return null;

  const armar = (): EtapaPlantilla[] | string => {
    const etapas: EtapaPlantilla[] = [];
    for (const [i, base] of plantilla.etapas.entries()) {
      const textoSemanas = semanas[i] ?? '';
      const nSemanas = textoSemanas.trim() === '' ? null : Number(textoSemanas);
      if (nSemanas !== null && !(Number.isInteger(nSemanas) && nSemanas >= 1)) return 'Las semanas deben ser un número entero.';
      const m = metasDeEstado(metas[i] ?? estadoDeMetas(base), base.tipo !== 'mantenimiento');
      if (typeof m === 'string') return `${NOMBRE_ETAPA[base.tipo]}: ${m}`;
      etapas.push({ ...base, semanas: nSemanas, ...m });
    }
    return etapas;
  };

  // Vista previa de las fechas; si algo no se puede programar, el motivo se
  // muestra al intentar crear el plan.
  const armado = armar();
  let vista: { etapas: EtapaProgramada[]; texto: string | null } | null = null;
  if (typeof armado !== 'string' && esFechaLocal(fechaInicio)) {
    try {
      const etapas = programar(armado, repetir, fechaInicio);
      const conPlan = etapas.map((e, i) => ({ ...e, id: String(i), planId: id }));
      const posicion = posicionEnPlan(conPlan, hoy);
      vista = { etapas, texto: posicion === null ? null : textoPosicion(posicion) };
    } catch {
      vista = null;
    }
  }

  async function crear(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    const etapas = armar();
    if (typeof etapas === 'string') return setError(etapas);
    setError(null);
    setGuardando(true);
    try {
      const nuevo = { id, nombre, fechaInicio, repetir, plantilla: etapas, notas: null };
      const revision = await revisarPlanNuevo(nuevo, hoy);
      if (revision.problema !== null) {
        setError(revision.problema);
        return;
      }
      if (revision.terminaActivo !== null && !confirmarTermino) {
        setPideConfirmar(true);
        setError(`Confirma que tu plan "${revision.terminaActivo.plan.nombre}" termina el ${formatearCortoConAnio(revision.terminaActivo.ultimoDia)}.`);
        return;
      }
      await crearPlan(nuevo, { terminarActivo: confirmarTermino, hoy });
      void navegar('/mas/plan', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  const primerCiclo = vista?.etapas.filter((e) => e.ciclo === 1) ?? [];
  const ultima = vista?.etapas[vista.etapas.length - 1];
  const totalCiclos = vista === null ? 0 : new Set(vista.etapas.map((e) => e.ciclo)).size;

  return (
    <div>
      <PageHeader
        antetitulo="Nuevo plan"
        titulo={`${plantilla.nombre}.`}
        descripcion={plantilla.descripcion}
        volver={{ a: '/mas/plan', texto: 'Mi plan' }}
      />

      {prellenar && (
        <div className="mb-5">
          <Banner tono="info" titulo="Prellenado con tu plan aprobado">
            Inicio el 12 de octubre de 2026; déficit de 1,500 kcal fijas, 140 g de proteína, 135 g de
            carbohidratos y 45 g de grasa; ayuno de 12 h. El mantenimiento usa el que tú captures.
            Revisa todo antes de crearlo.
          </Banner>
        </div>
      )}
      {activo !== null && (
        <div className="mb-5">
          <Banner tono="aviso" titulo={`Tienes un plan activo: ${activo.nombre}`}>
            El nuevo empieza hoy o después, y ese plan termina el día anterior. Lo que ya pasó se
            conserva con sus metas.
          </Banner>
        </div>
      )}

      <form onSubmit={(e) => void crear(e)} className="grid gap-[17px]" noValidate>
        <TextField etiqueta="Nombre del plan" valor={nombre} onCambio={setNombre} maxLength={60} />
        <DateField
          etiqueta="Fecha de inicio"
          valor={fechaInicio}
          onCambio={setFechaInicio}
          ayuda={
            activo === null
              ? 'Puede ser una fecha pasada: así el historial coincide.'
              : 'Hoy o después: los días que ya pasaron siguen con tu plan actual.'
          }
        />

        {plantilla.etapas.map((e, i) => (
          <Seccion key={i} titulo={`Etapa ${i + 1} · ${NOMBRE_ETAPA[e.tipo]}`}>
            <NumberField
              etiqueta="Semanas (vacío = sin fin)"
              valor={semanas[i] ?? ''}
              onCambio={(v) => setSemanas((s) => s.map((x, j) => (j === i ? v : x)))}
              decimal={false}
            />
            <CamposMetas
              tipo={e.tipo}
              estado={metas[i] ?? estadoDeMetas(e)}
              onCambio={(m) => setMetas((ms) => ms.map((x, j) => (j === i ? m : x)))}
            />
          </Seccion>
        ))}

        <Casilla marcada={repetir} onCambio={setRepetir}>
          Repetir el plan (se programa por lo menos un año)
        </Casilla>

        {vista !== null && (
          <Card>
            <span className="antetitulo">Así queda</span>
            <ul className="mt-2 space-y-1 text-[13px] text-texto">
              {primerCiclo.map((e) => (
                <li key={e.indice}>
                  {NOMBRE_ETAPA[e.tipo]}: {rango(e.fechaInicio, e.fechaFin)}
                </li>
              ))}
            </ul>
            {totalCiclos > 1 && ultima !== undefined && (
              <p className="mt-2 text-[12px] text-texto-suave">
                {totalCiclos} ciclos, hasta el {ultima.fechaFin === null ? 'final' : formatearCortoConAnio(ultima.fechaFin)}.
              </p>
            )}
            {vista.texto !== null && <p className="mt-2 text-[12px] text-texto-suave">Hoy: {vista.texto}</p>}
          </Card>
        )}

        {pideConfirmar && activo !== null && (
          <Casilla marcada={confirmarTermino} onCambio={setConfirmarTermino}>
            Sí, terminar "{activo.nombre}" el día anterior al inicio de este plan
          </Casilla>
        )}
        {error !== null && (
          <Banner tono="alerta" titulo="Todavía no se crea">
            {error}
          </Banner>
        )}
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Creando…' : 'Crear plan'}
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editar las metas de una etapa desde un día (Req. 21.12)
// ---------------------------------------------------------------------------

export function MetasEtapaPage() {
  const hoy = useFechaHoy();
  const datos = useLiveQuery(() => leerPlanes(), []);
  const [parametros] = useSearchParams();
  const indice = Number(parametros.get('indice') ?? '0');
  if (datos === undefined) return null;
  const plan = planActivo(datos.planes);
  const base = plan?.plantilla[indice];
  if (plan === null || base === undefined) {
    return (
      <div>
        <PageHeader titulo="Sin plan activo." volver={{ a: '/mas/plan', texto: 'Mi plan' }} />
        <EstadoVacio titulo="No hay metas que editar." descripcion="Crea un plan desde una plantilla." />
      </div>
    );
  }
  // Se parte de las metas de la etapa de ese tipo que cubre hoy, o de la plantilla
  const etapas = datos.etapas.filter((e) => e.planId === plan.id && e.indice === indice);
  const actual = etapaDe(etapas, hoy) ?? base;
  return <FormularioMetasEtapa key={`${plan.id}-${indice}`} plan={plan} indice={indice} tipo={base.tipo} inicial={estadoDeMetas(actual)} hoy={hoy} />;
}

function FormularioMetasEtapa({
  plan,
  indice,
  tipo,
  inicial,
  hoy,
}: {
  plan: Plan;
  indice: number;
  tipo: EtapaPlantilla['tipo'];
  inicial: EstadoMetas;
  hoy: FechaLocal;
}) {
  const navegar = useNavigate();
  const [estado, setEstado] = useState(inicial);
  const [desde, setDesde] = useState<string>(hoy);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    const metas = metasDeEstado(estado, tipo !== 'mantenimiento');
    if (typeof metas === 'string') return setError(metas);
    setError(null);
    setGuardando(true);
    try {
      await cambiarMetas(plan.id, indice, desde, metas, hoy);
      void navegar('/mas/plan', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={plan.nombre}
        titulo={`Metas de ${NOMBRE_ETAPA[tipo].toLowerCase()}.`}
        descripcion="Aplican desde el día que elijas a esta etapa y a las que vienen. Los días anteriores conservan sus metas."
        volver={{ a: '/mas/plan', texto: 'Mi plan' }}
      />
      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <CamposMetas tipo={tipo} estado={estado} onCambio={setEstado} />
        <DateField
          etiqueta="Aplicar desde"
          valor={desde}
          onCambio={setDesde}
          min={hoy}
          ayuda="Hoy o después: los días anteriores conservan sus metas."
        />
        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar metas'}
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cambiar el fin de una etapa y recorrer las siguientes (Req. 21.9)
// ---------------------------------------------------------------------------

const claveDe = (e: { ciclo: number; indice: number }) => `${e.ciclo}:${e.indice}`;

const textoDias = (n: number) => (n === 1 ? '1 día' : `${n} días`);

export function FinDeEtapaPage() {
  const hoy = useFechaHoy();
  const datos = useLiveQuery(() => leerPlanes(), []);
  const [parametros] = useSearchParams();
  if (datos === undefined) return null;
  const plan = planActivo(datos.planes);
  const delPlan = plan === null ? [] : datos.etapas.filter((e) => e.planId === plan.id);
  const cambiables = etapasConFinCambiable(delPlan, hoy);
  const pedida = cambiables.find(
    (e) => e.ciclo === Number(parametros.get('ciclo')) && e.indice === Number(parametros.get('indice')),
  );
  // Empieza con la etapa en curso; la que terminó ayer también se puede elegir
  const inicial = pedida ?? cambiables.find((e) => e.enCurso) ?? cambiables[0];
  if (plan === null || inicial === undefined) {
    return (
      <div>
        <PageHeader titulo="Sin etapas que cambiar." volver={{ a: '/mas/plan', texto: 'Mi plan' }} />
        <EstadoVacio
          titulo="No hay una etapa en curso ni por venir con fecha de fin."
          descripcion="Las etapas que ya terminaron no cambian."
        />
      </div>
    );
  }
  return (
    <FormularioFinDeEtapa
      key={`${plan.id}-${claveDe(inicial)}`}
      plan={plan}
      etapas={delPlan}
      otras={datos.etapas.filter((e) => e.planId !== plan.id)}
      planes={datos.planes}
      inicial={claveDe(inicial)}
      hoy={hoy}
    />
  );
}

function FormularioFinDeEtapa({
  plan,
  etapas,
  otras,
  planes,
  inicial,
  hoy,
}: {
  plan: Plan;
  etapas: Stage[];
  otras: Stage[];
  planes: Plan[];
  inicial: string;
  hoy: FechaLocal;
}) {
  const navegar = useNavigate();
  const cambiables = etapasConFinCambiable(etapas, hoy);
  const [elegida, setElegida] = useState(inicial);
  const etapa = cambiables.find((e) => claveDe(e) === elegida) ?? cambiables[0];
  const [ultimoDia, setUltimoDia] = useState<string>(etapa?.fin ?? hoy);
  const [quitarMetas, setQuitarMetas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (etapa === undefined) return null;
  const partes = etapas
    .filter((e) => e.ciclo === etapa.ciclo && e.indice === etapa.indice)
    .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));

  // Vista previa con la misma regla que se guarda (domain/etapas.ts)
  let cambio: CambioDeFin<Stage> | null = null;
  let problema: string | null = null;
  try {
    cambio = recorrerAlCambiarFin(etapas, etapa, ultimoDia, hoy);
    const traslape = primerTraslape(cambio.etapas, otras);
    if (traslape !== null) problema = mensajeTraslape(planes.find((p) => p.id === traslape.planId), traslape);
  } catch (e) {
    problema = e instanceof Error ? e.message : 'La fecha no es válida.';
  }
  const siguiente =
    cambio === null || !esFechaLocal(ultimoDia)
      ? undefined
      : cambio.etapas.find((e) => e.fechaInicio === sumarDias(ultimoDia, 1));
  const finNuevo = cambio === null ? null : finProgramado(cambio.etapas);
  const nombre = NOMBRE_ETAPA[etapa.tipo];
  // Metas editadas para días que dejan de ser de esta etapa: se quitan, y se dice antes
  const quitadas = cambio === null ? [] : partes.filter((p) => cambio.borrar.includes(p.id));
  const ultimaParte = partes[partes.length - 1];
  const faltaConfirmar = quitadas.length > 0 && !quitarMetas;

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando || etapa === undefined || faltaConfirmar) return;
    setError(null);
    setGuardando(true);
    try {
      await cambiarFinDeEtapa(plan.id, etapa, ultimoDia, hoy);
      void navegar('/mas/plan', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={plan.nombre}
        titulo="Cambiar el fin de una etapa."
        descripcion="Las etapas que siguen se recorren los mismos días, sin huecos ni traslapes. Lo que ya pasó no cambia."
        volver={{ a: '/mas/plan', texto: 'Mi plan' }}
      />
      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <SelectField
          etiqueta="Etapa"
          valor={elegida}
          onCambio={(v) => {
            const otra = cambiables.find((e) => claveDe(e) === v);
            if (otra === undefined) return;
            setElegida(v);
            setUltimoDia(otra.fin);
            setQuitarMetas(false);
            setError(null);
          }}
          opciones={cambiables.map((e) => ({
            valor: claveDe(e),
            etiqueta: `Ciclo ${e.ciclo} · ${NOMBRE_ETAPA[e.tipo]} · ${rango(e.inicio, e.fin)}${
              e.enCurso ? ' (en curso)' : e.terminoAyer ? ' (terminó ayer)' : ''
            }`,
          }))}
        />
        <DateField
          etiqueta="Último día de la etapa"
          valor={ultimoDia}
          onCambio={(v) => {
            setUltimoDia(v);
            setQuitarMetas(false);
          }}
          min={maxima(sumarDias(hoy, -1), etapa.inicio)}
          ayuda={`${etapa.terminoAyer ? 'Terminó' : 'Ahora termina'} el ${formatearCortoConAnio(etapa.fin)}. Puede ser ayer o después.`}
        />

        {cambio !== null && cambio.dias !== 0 && problema === null && (
          <Card>
            <span className="antetitulo">Así queda</span>
            <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-texto">
              <li>
                {nombre} del ciclo {etapa.ciclo}: {rango(etapa.inicio, ultimoDia)} (
                {cambio.dias > 0 ? `${textoDias(cambio.dias)} más` : `${textoDias(-cambio.dias)} menos`}).
              </li>
              {siguiente !== undefined && (
                <li>
                  Las etapas que siguen se recorren {textoDias(Math.abs(cambio.dias))}{' '}
                  {cambio.dias > 0 ? 'más tarde' : 'antes'}: {NOMBRE_ETAPA[siguiente.tipo].toLowerCase()} empieza el{' '}
                  {formatearCortoConAnio(siguiente.fechaInicio)}.
                </li>
              )}
              {finNuevo !== null && <li>Lo programado termina el {formatearCortoConAnio(finNuevo)}.</li>}
              {cambio.dias > 0 && ultimaParte !== undefined && (
                <li>
                  Los días que se agregan llevan las metas que la etapa tiene al final: {textoMetaKcal(ultimaParte.metaKcal)} ·{' '}
                  {textoMacros(ultimaParte)}.
                </li>
              )}
            </ul>
            <p className="mt-3 text-[12px] leading-relaxed text-texto-suave">
              Cada etapa conserva sus metas: los días que cambian de etapa toman las de su etapa nueva.
              {diferenciaDias(hoy, ultimoDia) < 0 ? ' Desde hoy aplican las metas de la etapa que sigue.' : ''} Tus
              comidas y registros no cambian.
            </p>
          </Card>
        )}

        {problema === null && quitadas.length > 0 && (
          <div className="space-y-3 rounded-panel bg-aviso-suave p-4">
            <p className="text-[13px] leading-relaxed text-texto">
              Se quitan las metas que pusiste para esta etapa desde{' '}
              {quitadas
                .map((p) => `el ${formatearCortoConAnio(p.fechaInicio)} (${textoMetaKcal(p.metaKcal)} · ${textoMacros(p)})`)
                .join(' y desde ')}
              : esos días pasan a la etapa que sigue. Las etapas de {nombre.toLowerCase()} que vienen conservan sus
              metas.
            </p>
            <Casilla marcada={quitarMetas} onCambio={setQuitarMetas}>
              Sí, quitar esas metas de esta etapa
            </Casilla>
          </div>
        )}
        {cambio !== null && cambio.dias === 0 && problema === null && (
          <p className="text-[13px] text-texto-suave">Es el mismo último día: no cambia nada.</p>
        )}

        {(problema !== null || error !== null) && (
          <Banner tono="alerta" titulo={error !== null ? 'No se guardó' : 'Revisa la fecha'}>
            {error ?? problema}
          </Banner>
        )}
        <Button
          type="submit"
          icono={Check}
          disabled={guardando || problema !== null || cambio === null || cambio.dias === 0 || faltaConfirmar}
        >
          {guardando ? 'Guardando…' : 'Guardar el cambio'}
        </Button>
      </form>
    </div>
  );
}
